import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {clearAllBodyScrollLocks, disableBodyScroll} from 'body-scroll-lock';
import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch,
  Configure,
  Hits,
  ClearRefinements,
  Index,
  Pagination,
  connectStateResults,
} from 'react-instantsearch-dom';
import qs from 'qs';

import CustomPortal from './CustomPortal';
import SearchHits from './SearchHits';
import CollectionHits from './CollectionHits';
import SearchHit from './SearchHit';
import ArticleHit from './ArticleHit';
import PageHit from './PageHit';
import CollectionHit from './CollectionHit';
import DebouncedSearchBox from './DebouncedSearchBox';
import DebouncedSearchBoxMobile from './DebouncedSearchBoxMobile';
import RefinementDropdown from './RefinementDropdown';
import SortBy from './SortBy';

const algoliaConfig = window.theme && window.theme.algolia;

if (!algoliaConfig.appid || !algoliaConfig.apikey) {
  window.console.error('You have not configured the algolia App ID and API Key in theme settings.');
}

const productIndex = `${algoliaConfig.indicesPrefix}products`;
const articlesIndex = `${algoliaConfig.indicesPrefix}articles`;
const pagesIndex = `${algoliaConfig.indicesPrefix}pages`;
const collectionsIndex = `${algoliaConfig.indicesPrefix}collections`;

const algoliaClient = algoliasearch(algoliaConfig.appid, algoliaConfig.apikey);
const searchClient = {
  search(requests) {
    if (requests.every(({params}) => !params.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
        })),
      });
    }

    /**
     * Exclude any pages with the metafield `hide_from_algolia`
     * unless the user is a `consultant`, `team_lead` or `staff`
     */
    if (
      window.theme.customerType !== 'consultant' &&
      window.theme.customerType !== 'team_lead' &&
      window.theme.customerType !== 'staff'
    ) {
      requests.forEach(request => {
        if (request.indexName === pagesIndex) {
          const excludeMetaFilter = 'NOT meta.thermomix.hide_from_algolia:true';
          if (request.params.filters) {
            request.params.filters = `${request.params.filters} AND ${excludeMetaFilter}`;
          } else {
            request.params.filters = excludeMetaFilter;
          }
        }
      });
    }
    /**
     * Exclude any products tagged with `discontinued`, `consultant_only` or `host_rewards`
     * unless the user is a `consultant`, `team_lead` or `staff`
     */
    if (
      window.theme.customerType !== 'consultant' &&
      window.theme.customerType !== 'team_lead' &&
      window.theme.customerType !== 'staff'
    ) {
      requests.forEach(request => {
        if (request.indexName === productIndex) {
          const excludeTagsFilter = 'NOT tags:discontinued AND NOT tags:consultant_only AND NOT tags:host_rewards';
          if (request.params.filters) {
            request.params.filters = `${request.params.filters} AND ${excludeTagsFilter}`;
          } else {
            request.params.filters = excludeTagsFilter;
          }
        }
      });
    }
    /**
     * Exclude any products tagged with `staff_only` unless the user is tagged `staff`
     */
    if (window.theme.customerType !== 'staff') {
      requests.forEach(request => {
        if (request.indexName === productIndex) {
          const excludeTagsFilter = 'NOT tags:staff_only';
          if (request.params.filters) {
            request.params.filters = `${request.params.filters} AND ${excludeTagsFilter}`;
          } else {
            request.params.filters = excludeTagsFilter;
          }
        }
      });
    }

    return algoliaClient.search(requests);
  },
};

/**
 * Dispatches the "search:close" event on the window.
 */
const dispatchSearchCloseEvent = event => {
  if (event.target !== event.currentTarget) return;
  const searchCloseEvent = new Event('search:close');
  window.dispatchEvent(searchCloseEvent);
};

/**
 * Quicksearch on desktop.
 */
const QuickSearchDesktop = () => {
  /**
   * Add a search event listener for the click event on the search-modal element.
   * If the user clicks on the dark underlay of the modal, we trigger the 'search:close'
   * event which resets the interal values of InstantSearch and hides the modal.
   */
  useEffect(() => {
    const searchModal = document.querySelector('.search-modal');
    if (!searchModal) return;

    searchModal.addEventListener('click', dispatchSearchCloseEvent);

    return () => {
      searchModal.removeEventListener('click', dispatchSearchCloseEvent);
    };
  }, []);

  return (
    <InstantSearch indexName={productIndex} searchClient={searchClient}>
      <Configure hitsPerPage={8} distinct />
      <CustomPortal domSelector="[data-header-search-input]">
        <DebouncedSearchBox showModal={showModal} hideModal={hideModal} />
      </CustomPortal>
      <div className="search-modal__results container">
        <Index indexName={collectionsIndex}>
          <CollectionHits>
            <Hits hitComponent={CollectionHit} />
          </CollectionHits>
        </Index>
        <Index indexName={productIndex}>
          <SearchHits>
            <Hits hitComponent={SearchHit} />
          </SearchHits>
        </Index>
      </div>
    </InstantSearch>
  );
};
const searchResultsDesktopEl = document.querySelector('[data-search-results-desktop]');
if (searchResultsDesktopEl) {
  ReactDOM.render(<QuickSearchDesktop />, searchResultsDesktopEl);
}

/**
 * Quicksearch on mobile.
 */

const QuickSearchMobile = () => (
  <InstantSearch indexName={productIndex} searchClient={searchClient}>
    <Configure hitsPerPage={4} distinct />
    <DebouncedSearchBoxMobile />
    <div className="search-modal__results search-modal__results__mobile container">
        <Index indexName={productIndex}>
          <SearchHits>
            <Hits hitComponent={SearchHit} />
          </SearchHits>
        </Index>
        <Index indexName={collectionsIndex}>
          <CollectionHits>
            <Hits hitComponent={CollectionHit} />
          </CollectionHits>
        </Index>
    </div>
  </InstantSearch>
);

const serchResultsMobileEl = document.querySelector('[data-search-results-mobile]');
if (serchResultsMobileEl) {
  ReactDOM.render(<QuickSearchMobile />, serchResultsMobileEl);
}

function hideModal() {
  const searchModal = document.querySelector('.search-modal');
  if (!searchModal) return;

  clearAllBodyScrollLocks();
  document.body.classList.remove('search-is-visible');
  searchModal.setAttribute('hidden', 'hidden');
  window.searchIsVisible = false;
}

function showModal() {
  const searchModal = document.querySelector('.search-modal');
  if (!searchModal) return;

  disableBodyScroll(document.querySelector('header'));
  document.body.classList.add('search-is-visible');
  searchModal.removeAttribute('hidden');
  window.searchIsVisible = true;
}

/**
 * Search Page
 */

const createURL = state => `?${qs.stringify(state)}`;

const searchStateToUrl = ({location}, searchState) => {
  return searchState ? `${location.pathname}${createURL(searchState)}` : '';
};

const urlToSearchState = ({search}) => qs.parse(search.slice(1));

const SetActiveTabOnStateChange = connectStateResults(({setTab, searching, allSearchResults}) => {
  if (allSearchResults) {
    const tabs = [productIndex, articlesIndex, pagesIndex];
    const activeTab = tabs.find(tab => allSearchResults[tab] && allSearchResults[tab].nbHits > 0);

    if (searching && activeTab) {
      setTab(activeTab.replace(algoliaConfig.indicesPrefix, ''));
    }
  }
  return null;
});

const StateResults = ({index, allSearchResults}) => {
  const nbHits = (allSearchResults && allSearchResults[index] && allSearchResults[index].nbHits) || 0;
  return <span>({nbHits})</span>;
};

const CustomStateResults = connectStateResults(StateResults);

const SearchPage = () => {
  const TABS = {
    PRODUCTS: 'products',
    ARTICLES: 'articles',
    PAGES: 'pages',
  };
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [tab, setTab] = useState(TABS.PRODUCTS);

  const [searchState, setSearchState] = useState(urlToSearchState(window.location));
  const [debouncedSetState, setDebouncedSetState] = useState(null);

  const onSearchStateChange = updatedSearchState => {
    clearTimeout(debouncedSetState);

    const newQueryState = {
      query: updatedSearchState.query,
    };

    setDebouncedSetState(
      setTimeout(() => {
        window.history.pushState(updatedSearchState, null, searchStateToUrl(window, newQueryState));
      }, 500)
    );

    setSearchState(updatedSearchState);
  };

  return (
    <InstantSearch
      indexName={productIndex}
      searchClient={searchClient}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
      createURL={createURL}
    >
      <Configure hitsPerPage={12} distinct />
      <CustomPortal domSelector="[data-search-page-input]">
        <DebouncedSearchBox />
      </CustomPortal>
      <SetActiveTabOnStateChange setTab={setTab} />
      <Index indexName={productIndex}>
        {tab === TABS.PRODUCTS && (
          <div className="search-page__filters-mobile large-up--hide">
            <button
              className="search-page__mobile-filter-toggle"
              onClick={() => {
                setShowMobileFilters(true);
              }}
            >
              Filters
            </button>
          </div>
        )}
        <div className="search-page__tabs-sort">
          <div className="search-page__tabs">
            <button
              className={`search-page__tabs-button ${tab === TABS.PRODUCTS ? 'active' : ''}`}
              onClick={() => setTab(TABS.PRODUCTS)}
            >
              Products <CustomStateResults index={productIndex} />
            </button>
            <button
              className={`search-page__tabs-button ${tab === TABS.ARTICLES ? 'active' : ''}`}
              onClick={() => setTab(TABS.ARTICLES)}
            >
              Articles <CustomStateResults index={articlesIndex} />
            </button>
            <button
              className={`search-page__tabs-button ${tab === TABS.PAGES ? 'active' : ''}`}
              onClick={() => setTab(TABS.PAGES)}
            >
              Pages <CustomStateResults index={pagesIndex} />
            </button>
          </div>
          <div className="search-page__sort large-down--hide">
            <SortBy
              items={[
                {value: 'products', label: 'Featured'},
                {value: 'products_alphabetically_az', label: 'Name: A-Z'},
                {value: 'products_alphabetically_za', label: 'Name: Z-A'},
                {value: 'products_price_ascending', label: 'Price: Low - High'},
                {value: 'products_price_descending', label: 'Price: High - Low'},
                {value: 'products_created_new', label: 'Newest'},
              ]}
              defaultRefinement={productIndex}
            />
          </div>
        </div>
        <div className="tab" hidden={tab !== TABS.PRODUCTS}>
          <div className={`search-page__filters ${!showMobileFilters ? 'large-down--hide' : ''}`}>
            <div className="search-page__filters-header m-b-md">
              <p className="search-page__filter-by h3 m-b-0">Filter by</p>
              <ClearRefinements />
            </div>
            <div className="search-page__dropdowns">
              <RefinementDropdown attribute="product_type" facetLabel="Product Type" />
              <RefinementDropdown attribute="vendor" facetLabel="Brand" />
              <RefinementDropdown attribute="price" facetLabel="Price" />
            </div>
            <button
              className="search-page__mobile-filter-apply large-up--hide"
              onClick={() => {
                setShowMobileFilters(false);
              }}
            >
              Apply
            </button>
          </div>

          <SearchHits>
            <Hits hitComponent={SearchHit} />
          </SearchHits>
          <div className="pagination pagination--products">
            <Pagination showFirst={false} showLast={false} showNext={false} showPrevious={false} />
          </div>
        </div>
      </Index>
      <Index indexName={articlesIndex}>
        <div className="tab" hidden={tab !== TABS.ARTICLES}>
          <SearchHits>
            <Hits hitComponent={ArticleHit} />
          </SearchHits>
          <div className="pagination pagination--articles">
            <Pagination showFirst={false} showLast={false} showNext={false} showPrevious={false} />
          </div>
        </div>
      </Index>
      <Index indexName={pagesIndex}>
        <div className="tab" hidden={tab !== TABS.PAGES}>
          <SearchHits>
            <Hits hitComponent={PageHit} />
          </SearchHits>
          <div className="pagination pagination--pages">
            <Pagination showFirst={false} showLast={false} showNext={false} showPrevious={false} />
          </div>
        </div>
      </Index>
    </InstantSearch>
  );
};

const searchPageResultsEl = document.querySelector('[data-search-page-results]');
if (searchPageResultsEl) {
  ReactDOM.render(<SearchPage />, searchPageResultsEl);
}
